import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  getTables,
  getTableColumns,
  getTablePrimaryKeys,
  getTableData,
  createRecord,
  updateRecord,
  deleteRecord,
  searchRecords,
  TableInfo,
  ColumnInfo,
  RecordCreate,
  RecordUpdate,
  RecordDelete,
  SearchRequest,
} from '../services/api';
import DateField from './DateField';

export default function DataManagement() {
  const { t } = useTranslation();
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState<TableInfo | null>(null);
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [primaryKeys, setPrimaryKeys] = useState<string[]>([]);
  const [data, setData] = useState<any>({ columns: [], rows: [], limit: 100, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [deletingRowIndex, setDeletingRowIndex] = useState<number | null>(null);
  const [searchColumn, setSearchColumn] = useState('');
  const [searchValue, setSearchValue] = useState('');
  
  // Success message
  const [successMessage, setSuccessMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    fetchTables();
  }, []);

  useEffect(() => {
    if (selectedTable) {
      fetchTableDetails();
    }
  }, [selectedTable]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const tablesData = await getTables();
      setTables(tablesData);
      if (tablesData.length > 0 && !selectedTable) {
        setSelectedTable(tablesData[0]);
      }
    } catch (err: any) {
      setError(t('dataManagement.failedToFetch'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTableDetails = async () => {
    if (!selectedTable) return;
    
    try {
      setLoading(true);
      const [columnsData, tableData, primaryKeysData] = await Promise.all([
        getTableColumns(selectedTable.schema, selectedTable.name),
        getTableData(selectedTable.schema, selectedTable.name, 100, 0),
        getTablePrimaryKeys(selectedTable.schema, selectedTable.name)
      ]);
      
      setColumns(columnsData);
      setData(tableData);
      setPrimaryKeys(primaryKeysData);
    } catch (err: any) {
      setError(t('dataManagement.failedToFetchDetails'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRecord = async () => {
    if (!selectedTable) return;
    
    try {
      const record: RecordCreate = {
        table: selectedTable.name,
        schema: selectedTable.schema,
        data: formData
      };
      
      await createRecord(record);
      setSuccessMessage(t('dataManagement.recordCreated'));
      setSnackbarOpen(true);
      setCreateDialogOpen(false);
      setFormData({});
      fetchTableDetails();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('dataManagement.failedToCreate'));
    }
  };

  const handleUpdateRecord = async () => {
    if (!selectedTable || editingRowIndex === null) return;
    
    try {
      const row = data.rows[editingRowIndex];
      
      // Use first primary key column for record identification
      if (primaryKeys.length !== 1) {
        setError('Only single-column primary keys are supported for editing. Use raw SQL for composite keys.');
        return;
      }
      const pkCol = primaryKeys[0];
      const pkValue = row[data.columns.indexOf(pkCol)];

      const record: RecordUpdate = {
        table: selectedTable.name,
        schema: selectedTable.schema,
        record_id: pkValue,
        id_column: pkCol,
        data: formData
      };

      await updateRecord(record);
      setSuccessMessage(t('dataManagement.recordUpdated'));
      setSnackbarOpen(true);
      setEditDialogOpen(false);
      setFormData({});
      setEditingRowIndex(null);
      fetchTableDetails();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('dataManagement.failedToUpdate'));
    }
  };

  const handleDeleteRecord = async () => {
    if (!selectedTable || deletingRowIndex === null) return;
    
    try {
      const row = data.rows[deletingRowIndex];
      
      // Use first primary key column for record identification
      if (primaryKeys.length !== 1) {
        setError('Only single-column primary keys are supported for deletion. Use raw SQL for composite keys.');
        return;
      }
      const pkCol = primaryKeys[0];
      const pkValue = row[data.columns.indexOf(pkCol)];

      const record: RecordDelete = {
        table: selectedTable.name,
        schema: selectedTable.schema,
        record_id: pkValue,
        id_column: pkCol
      };

      await deleteRecord(record);
      setSuccessMessage(t('dataManagement.recordDeleted'));
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setDeletingRowIndex(null);
      fetchTableDetails();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('dataManagement.failedToDelete'));
    }
  };

  const handleSearch = async () => {
    if (!selectedTable || !searchColumn || !searchValue) return;
    
    try {
      const search: SearchRequest = {
        table: selectedTable.name,
        schema: selectedTable.schema,
        search_column: searchColumn,
        search_value: searchValue,
        limit: 100
      };
      
      const searchResults = await searchRecords(search);
      setData({ ...searchResults, limit: 100, offset: 0 });
      setSearchDialogOpen(false);
      setSuccessMessage(`Found ${searchResults.row_count} results`);
      setSnackbarOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('dataManagement.searchFailed'));
    }
  };

  const openEditDialog = (rowIndex: number) => {
    const row = data.rows[rowIndex];
    const rowData: Record<string, any> = {};
    
    data.columns.forEach((column: string, index: number) => {
      rowData[column] = row[index];
    });
    
    setFormData(rowData);
    setEditingRowIndex(rowIndex);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (rowIndex: number) => {
    setDeletingRowIndex(rowIndex);
    setDeleteDialogOpen(true);
  };

  const openCreateDialog = () => {
    const initialData: Record<string, any> = {};
    columns.forEach(col => {
      // Exclude primary keys for tables with SERIAL/auto-increment IDs, but include them for composite keys like cats table
      const isAutoGeneratedPK = col.column_name === 'id' && col.column_default?.includes('nextval');
      if (!isAutoGeneratedPK && col.column_name !== 'created_at' && col.column_name !== 'updated_at') {
        initialData[col.column_name] = '';
      }
    });
    setFormData(initialData);
    setCreateDialogOpen(true);
  };

  const resetSearch = () => {
    setSearchColumn('');
    setSearchValue('');
    fetchTableDetails();
  };

  if (loading && !selectedTable) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('dataManagement.title')}
      </Typography>

      <Typography variant="body1" color="text.secondary" gutterBottom>
        {t('dataManagement.description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table Selection */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('dataManagement.selectTable')}</InputLabel>
              <Select
                value={selectedTable ? `${selectedTable.schema}.${selectedTable.name}` : ''}
                onChange={(e) => {
                  const tableKey = e.target.value;
                  const table = tables.find(t => `${t.schema}.${t.name}` === tableKey);
                  setSelectedTable(table || null);
                }}
              >
                {tables.map((table) => (
                  <MenuItem key={`${table.schema}.${table.name}`} value={`${table.schema}.${table.name}`}>
                    {table.schema}.{table.name}
                    {table.row_count !== null && (
                      <Chip
                        label={`${table.row_count} ${t('common.rows')}`}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
                disabled={!selectedTable}
              >
                {t('common.addRecord')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => setSearchDialogOpen(true)}
                disabled={!selectedTable}
              >
                {t('common.search')}
              </Button>
              <IconButton
                onClick={resetSearch}
                disabled={!selectedTable}
                title={t('dataManagement.refreshData')}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      {selectedTable && (
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #ddd' }}>
            <Typography variant="h6">
              {selectedTable.schema}.{selectedTable.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data.row_count} {t('common.records')}
            </Typography>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.actions')}</TableCell>
                    {data.columns.map((column: string, index: number) => (
                      <TableCell key={index}>
                        <strong>{column}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.map((row: any[], rowIndex: number) => (
                    <TableRow key={rowIndex} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(rowIndex)}
                            title={t('common.edit')}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openDeleteDialog(rowIndex)}
                            title={t('common.delete')}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      {row.map((cell: any, cellIndex: number) => (
                        <TableCell key={cellIndex}>
                          {cell !== null ? String(cell) : <em style={{ color: '#999' }}>NULL</em>}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Create Record Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dataManagement.createNewRecord')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {columns.filter(col => col.column_name !== 'id' && col.column_name !== 'created_at' && col.column_name !== 'updated_at').map((column) => {
              // Special handling for sex field
              if (column.column_name === 'sex') {
                return (
                  <FormControl fullWidth margin="normal" key={column.column_name}>
                    <InputLabel>{t('catFields.sex')}</InputLabel>
                    <Select
                      value={formData[column.column_name] || ''}
                      label={t('catFields.sex')}
                      onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>{t('common.none')}</em>
                      </MenuItem>
                      <MenuItem value="M">M</MenuItem>
                      <MenuItem value="F">F</MenuItem>
                    </Select>
                  </FormControl>
                );
              }

              // Special handling for age field
              if (column.column_name === 'age') {
                return (
                  <FormControl fullWidth margin="normal" key={column.column_name}>
                    <InputLabel>{t('catFields.age')}</InputLabel>
                    <Select
                      value={formData[column.column_name] || ''}
                      label={t('catFields.age')}
                      onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>{t('common.none')}</em>
                      </MenuItem>
                      <MenuItem value="Kitten">{t('catAges.kitten')}</MenuItem>
                      <MenuItem value="Adult">{t('catAges.adult')}</MenuItem>
                      <MenuItem value="Senior">{t('catAges.senior')}</MenuItem>
                    </Select>
                  </FormControl>
                );
              }

              // Special handling for source field
              if (column.column_name === 'source') {
                return (
                  <FormControl fullWidth margin="normal" key={column.column_name}>
                    <InputLabel>{t('catFields.source')}</InputLabel>
                    <Select
                      value={formData[column.column_name] || ''}
                      label={t('catFields.source')}
                      onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>{t('common.none')}</em>
                      </MenuItem>
                      <MenuItem value="ASL">{t('catSources.asl')}</MenuItem>
                      <MenuItem value="Private">{t('catSources.private')}</MenuItem>
                      <MenuItem value="Municipality">{t('catSources.municipality')}</MenuItem>
                    </Select>
                  </FormControl>
                );
              }

              // Special handling for Y/N fields (vaccine, sterilized, fiv_felv, delivery)
              if (['vaccine', 'sterilized', 'fiv_felv', 'delivery'].includes(column.column_name)) {
                return (
                  <FormControl fullWidth margin="normal" key={column.column_name}>
                    <InputLabel>{column.column_name}</InputLabel>
                    <Select
                      value={formData[column.column_name] || ''}
                      label={column.column_name}
                      onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>{t('common.none')}</em>
                      </MenuItem>
                      <MenuItem value="Y">Y</MenuItem>
                      <MenuItem value="N">N</MenuItem>
                    </Select>
                  </FormControl>
                );
              }

              // Special handling for date fields
              if (column.data_type === 'date' || column.column_name.includes('_date') || column.column_name.includes('date_')) {
                return (
                  <DateField
                    key={column.column_name}
                    label={column.column_name}
                    value={formData[column.column_name] || ''}
                    onChange={(v) => setFormData({ ...formData, [column.column_name]: v })}
                  />
                );
              }

              // Default text field for other types
              return (
                <TextField
                  key={column.column_name}
                  fullWidth
                  margin="normal"
                  label={column.column_name}
                  value={formData[column.column_name] || ''}
                  onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
                  helperText={`${column.data_type} - ${column.is_nullable === 'YES' ? 'Optional' : 'Required'}`}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreateRecord}>{t('common.create')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Record Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dataManagement.editRecord')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {columns.filter(col => col.column_name !== 'id' && col.column_name !== 'created_at' && col.column_name !== 'updated_at').map((column) => {
              // Special handling for sex field
              if (column.column_name === 'sex') {
                return (
                  <FormControl fullWidth margin="normal" key={column.column_name}>
                    <InputLabel>{t('catFields.sex')}</InputLabel>
                    <Select
                      value={formData[column.column_name] || ''}
                      label={t('catFields.sex')}
                      onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>{t('common.none')}</em>
                      </MenuItem>
                      <MenuItem value="M">M</MenuItem>
                      <MenuItem value="F">F</MenuItem>
                    </Select>
                  </FormControl>
                );
              }

              // Special handling for age field
              if (column.column_name === 'age') {
                return (
                  <FormControl fullWidth margin="normal" key={column.column_name}>
                    <InputLabel>{t('catFields.age')}</InputLabel>
                    <Select
                      value={formData[column.column_name] || ''}
                      label={t('catFields.age')}
                      onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>{t('common.none')}</em>
                      </MenuItem>
                      <MenuItem value="Kitten">{t('catAges.kitten')}</MenuItem>
                      <MenuItem value="Adult">{t('catAges.adult')}</MenuItem>
                      <MenuItem value="Senior">{t('catAges.senior')}</MenuItem>
                    </Select>
                  </FormControl>
                );
              }

              // Special handling for source field
              if (column.column_name === 'source') {
                return (
                  <FormControl fullWidth margin="normal" key={column.column_name}>
                    <InputLabel>{t('catFields.source')}</InputLabel>
                    <Select
                      value={formData[column.column_name] || ''}
                      label={t('catFields.source')}
                      onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>{t('common.none')}</em>
                      </MenuItem>
                      <MenuItem value="ASL">{t('catSources.asl')}</MenuItem>
                      <MenuItem value="Private">{t('catSources.private')}</MenuItem>
                      <MenuItem value="Municipality">{t('catSources.municipality')}</MenuItem>
                    </Select>
                  </FormControl>
                );
              }

              // Special handling for Y/N fields (vaccine, sterilized, fiv_felv, delivery)
              if (['vaccine', 'sterilized', 'fiv_felv', 'delivery'].includes(column.column_name)) {
                return (
                  <FormControl fullWidth margin="normal" key={column.column_name}>
                    <InputLabel>{column.column_name}</InputLabel>
                    <Select
                      value={formData[column.column_name] || ''}
                      label={column.column_name}
                      onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>{t('common.none')}</em>
                      </MenuItem>
                      <MenuItem value="Y">Y</MenuItem>
                      <MenuItem value="N">N</MenuItem>
                    </Select>
                  </FormControl>
                );
              }

              // Special handling for date fields
              if (column.data_type === 'date' || column.column_name.includes('_date') || column.column_name.includes('date_')) {
                return (
                  <DateField
                    key={column.column_name}
                    label={column.column_name}
                    value={formData[column.column_name] || ''}
                    onChange={(v) => setFormData({ ...formData, [column.column_name]: v })}
                  />
                );
              }

              // Default text field for other types
              return (
                <TextField
                  key={column.column_name}
                  fullWidth
                  margin="normal"
                  label={column.column_name}
                  value={formData[column.column_name] || ''}
                  onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
                  helperText={`${column.data_type} - ${column.is_nullable === 'YES' ? 'Optional' : 'Required'}`}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleUpdateRecord}>{t('common.update')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('dataManagement.confirmDeleteRecord')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDeleteRecord}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Search Dialog */}
      <Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dataManagement.searchRecords')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('dataManagement.searchColumn')}</InputLabel>
              <Select
                value={searchColumn}
                onChange={(e) => setSearchColumn(e.target.value)}
              >
                {columns.map((column) => (
                  <MenuItem key={column.column_name} value={column.column_name}>
                    {column.column_name} ({column.data_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label={t('dataManagement.searchValue')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              helperText={t('dataManagement.searchHint')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSearch} disabled={!searchColumn || !searchValue}>
            {t('common.search')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}